import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { ArrowLeft } from '@phosphor-icons/react';
import LeadSearchForm from './leadSearch/LeadSearchForm';
import LeadSearchResults from './leadSearch/LeadSearchResults';
import { loadPageSize, savePageSize } from '@/components/PageSizeSelect';

const EMPTY_FORM = {
  leadId: '',
  consultantId: '', consultantName: '',
  leadType: '', leadOrigin: '', description: '',
  clientId: '', clientName: '', clientEmail: '', clientPhoneNumber: '', clientCity: '',
  companyId: '', companyName: '', companyEmail: '', companyPhoneNumber: '', companyCity: '',
  initDate: '', endDate: '', value: '', ref: '', orderId: '', observations: '',
  contactState: '', contactInitDate: '', contactEndDate: '', contactResult: '', contactObservation: '',
  profundidadeRede: 1,
};

export default function LeadSearchPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_FORM);
  const [results, setResults] = useState(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(loadPageSize);
  const [leadTypes, setLeadTypes] = useState([]);
  const [leadOrigins, setLeadOrigins] = useState([]);
  const [leadResults, setLeadResults] = useState([]);
  const [leadEvents, setLeadEvents] = useState([]);
  // Selection & bulk evolve
  const [selected, setSelected] = useState(new Set());
  const [showEvolve, setShowEvolve] = useState(false);
  const [evolveForm, setEvolveForm] = useState({ ID_Evento: '', ID_Resultado: '', Observacoes: '' });
  const [evolveEvents, setEvolveEvents] = useState([]);
  const [evolveResults, setEvolveResults] = useState([]);
  const [evolving, setEvolving] = useState(false);
  const [evolveMsg, setEvolveMsg] = useState(null);

  // Load lead reference data once
  useEffect(() => {
    Promise.all([
      api.post('/leads/types').then(r => setLeadTypes(r.data || [])).catch(() => {}),
      api.post('/leads/origins').then(r => setLeadOrigins(r.data || [])).catch(() => {}),
      api.post('/leads/results').then(r => setLeadResults(r.data || [])).catch(() => {}),
    ]);
  }, []);

  // Load events when leadType changes
  useEffect(() => {
    if (form.leadType) {
      api.post('/leads/events', { TipoLead: form.leadType }).then(r => setLeadEvents(r.data || [])).catch(() => {});
    } else {
      setLeadEvents([]);
    }
  }, [form.leadType]);

  const sf = (field, val) => setForm(prev => ({ ...prev, [field]: val }));

  const handleSearch = (p = 1, size = pageSize) => {
    setLoading(true);
    setSelected(new Set());
    setShowEvolve(false);
    setEvolveMsg(null);
    api.post('/leads/advancedSearch', { ...form, page: p, pageSize: size })
      .then(r => {
        setResults(r.data?.data || []);
        setTotal(r.data?.total || 0);
        setTotalPages(r.data?.totalPages || 0);
        setPage(r.data?.page || p);
        setLoading(false);
      })
      .catch(() => { setResults([]); setLoading(false); });
  };

  const handlePageSizeChange = (v) => {
    savePageSize(v);
    setPageSize(v);
    if (results !== null) handleSearch(1, v);
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!results) return;
    if (selected.size === results.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(results.map(r => r.ID_Negocio)));
    }
  };

  const openEvolve = async () => {
    if (selected.size === 0) return;
    setShowEvolve(true);
    setEvolveMsg(null);
    setEvolveForm({ ID_Evento: '', ID_Resultado: '', Observacoes: '' });

    // Bulk-evolve requires all selected leads to share one TipoLead so we can
    // derive a common set of next-state options (Ordem >= max current Ordem).
    const selectedLeads = results.filter(r => selected.has(r.ID_Negocio));
    const tipoLeads = [...new Set(selectedLeads.map(r => r.TipoLead).filter(Boolean))];

    if (tipoLeads.length !== 1) {
      setEvolveEvents([]);
      setEvolveMsg('As leads seleccionadas têm tipos diferentes. Seleccione leads do mesmo tipo.');
      return;
    }

    const maxOrdem = Math.max(...selectedLeads.map(r => r.OrdemAtual || 0));
    try {
      const [evR, resR] = await Promise.all([
        api.post('/leads/events', { TipoLead: tipoLeads[0] }),
        api.post('/leads/results'),
      ]);
      setEvolveEvents((evR.data || []).filter(e => e.Ordem >= maxOrdem));
      setEvolveResults(resR.data || []);
    } catch {
      setEvolveEvents([]);
    }
  };

  const handleEvolve = async () => {
    if (!evolveForm.ID_Evento || !evolveForm.ID_Resultado) {
      setEvolveMsg('Seleccione o estado e o resultado');
      return;
    }
    setEvolving(true);
    setEvolveMsg(null);
    const selectedLeads = results.filter(r => selected.has(r.ID_Negocio));
    // Hora local do browser (Portugal). Não usar `toISOString()` — devolve
    // UTC e as intervenções ficariam com hora atrasada em 1h.
    const p = (n) => String(n).padStart(2, '0');
    const localDt = (offsetMs = 0) => {
      const d = new Date(Date.now() + offsetMs);
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}` +
             `T${p(d.getHours())}:${p(d.getMinutes())}`;
    };
    const now = localDt();
    const later = localDt(15 * 60000);
    try {
      const res = await api.post('/negocios/bulkAddState', {
        leads: selectedLeads.map(r => ({ ID_Negocio: r.ID_Negocio, NomePessoa: r.NomeCliente || '', ContactoPessoa: '' })),
        ID_Evento: evolveForm.ID_Evento,
        ID_Resultado: evolveForm.ID_Resultado,
        Observacoes: evolveForm.Observacoes,
        DataInicio: now,
        DataFim: later,
      });
      const d = res.data;
      setEvolveMsg(`Processadas ${d.total}: ${d.ok} com sucesso, ${d.failed} com erro`);
      if (d.ok > 0) {
        setSelected(new Set());
        handleSearch(page);
      }
    } catch {
      setEvolveMsg('Erro ao processar evolução');
    }
    setEvolving(false);
  };

  return (
    <div data-testid="lead-search-page" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <button data-testid="back-btn" onClick={() => navigate('/leads')} className="flex items-center gap-1.5 text-sm text-[#0F1829] hover:underline mb-4">
        <ArrowLeft size={16} /> Voltar às Leads
      </button>
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>Pesquisa de Leads</h1>
        <p className="text-sm text-[#6B7280] mt-1">Pesquisa avançada com todos os critérios</p>
      </div>

      <LeadSearchForm
        form={form}
        sf={sf}
        leadTypes={leadTypes}
        leadOrigins={leadOrigins}
        leadEvents={leadEvents}
        leadResults={leadResults}
        loading={loading}
        onSearch={() => handleSearch(1)}
      />

      <LeadSearchResults
        results={results}
        total={total}
        totalPages={totalPages}
        page={page}
        selected={selected}
        toggleSelect={toggleSelect}
        toggleSelectAll={toggleSelectAll}
        showEvolve={showEvolve}
        evolveEvents={evolveEvents}
        evolveResults={evolveResults}
        evolveForm={evolveForm}
        setEvolveForm={setEvolveForm}
        evolving={evolving}
        evolveMsg={evolveMsg}
        onOpenEvolve={openEvolve}
        onEvolveConfirm={handleEvolve}
        onEvolveCancel={() => { setShowEvolve(false); setEvolveMsg(null); }}
        onGoPage={handleSearch}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
}
